import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import {
  DateTimeTzSchema,
  GenericTimelyAccountId,
  GenericTimelyUserId,
  GenericUserID,
  GenericUUID,
  YYYYMMDDSchema,
} from '../models.generic';

extendZodWithOpenApi(z);

/**
 * @see  ai_prediction_logs.transformers.ts:transformRecords`
 *       as an example of how these groups of enums matter in determining final state.
 */

export const aiLogInferenceEventEnums = [
  'inference_complete',
  'inference_failed',
  'inference_requested',
  'inference_pending', // Not in DB, only client side. Set to when *_requested will likely be fired.
] as const;

export const aiLogTrainingEventEnums = [
  'training_requested',
  'training_pending', // Not in DB, only client side. Set to when *_requested will likely be fired.
  'training_running',
  'training_complete',
  'training_failed',
  'training_data_insufficient', // New event for not enough data yet
] as const;

export const aiLogModelResetEventEnums = ['model_reset'] as const;
export const aiLogModelHealthEventEnums = ['model_health'] as const;

const allEventEnums = [
  ...aiLogInferenceEventEnums,
  ...aiLogTrainingEventEnums,
  ...aiLogModelResetEventEnums,
  ...aiLogModelHealthEventEnums,
  'training_uninitialized', // Not in DB, only payloads atm
  'inference_uninitialized', // Not in DB, only payloads atm
] as const;

export const AiLogEventSchema = z
  .enum(allEventEnums)
  .describe('The type of AI event that occurred');
export type AiLogEventEnum = z.infer<typeof AiLogEventSchema>;

export const IntervalPredictionEnumSchema = z.enum(aiLogInferenceEventEnums);

export const TrainingSummaryEnumSchema = z.enum(aiLogTrainingEventEnums);
export type TrainingSummaryEnum = z.infer<typeof TrainingSummaryEnumSchema>;

/**
 * ~Database record.
 */
export type AiPredictionLogsWrite = z.infer<typeof AiPredictionLogsWriteSchema>;
export const AiPredictionLogsWriteSchema = z.object({
  id: GenericUUID.optional(),
  user_id: GenericUserID,
  timely_user_id: GenericTimelyUserId,
  timely_account_id: GenericTimelyAccountId,
  day: YYYYMMDDSchema.nullable(),
  event: AiLogEventSchema,
  message: z
    .string()
    .nullable()
    .optional()
    .describe('Additional information about the event')
    .openapi({ example: 'Model is up to date' }),
  updated_at: z.string().datetime({ offset: true }),
  conflict_key: z.string(),
});

export type CreateTrainingSummary = z.infer<typeof CreateTrainingSummarySchema>;
export const CreateTrainingSummarySchema = AiPredictionLogsWriteSchema.pick({
  timely_user_id: true,
  timely_account_id: true,
  message: true,
}).extend({
  event: TrainingSummaryEnumSchema,
  model_last_trained_at: z.string().datetime({ offset: true }).optional(),
});

// export const AiModelStateEnumSchema = z.enum(['ok', 'failed', 'untrained']);
// export type AiModelStateEnum = z.infer<typeof AiModelStateEnumSchema>;

export type AiLog = z.infer<typeof AiLogSchema>;
export const AiLogSchema = AiPredictionLogsWriteSchema.pick({
  day: true,
  updated_at: true,
  event: true,
  message: true,
});

export const AiStatusLogSchema = AiLogSchema.extend({
  at: z
    .string()
    .datetime({ offset: true })
    .openapi({
      example: '2024-03-18 07:00:00+00',
      description: 'When did this `event` occur?',
    })
    .nullable(),
});

export type AiStatusLog = z.infer<typeof AiStatusLogSchema>;

/**
 *
 * The AiStatusLogs are used to derive the current state of the AI.
 * @see - derive-ai-status.ts
 *
 */

const aiCurrentStatusEnums = [
  'DISABLED',
  'INFERENCE_PENDING', // Added for pending inference, never set server-side
  'INFERENCE_RUNNING',
  'NOT_SUPPORTED',
  'RETRY_INFERENCE',
  'WAITING_AFTER_FAIL',
  'FIRST_INFERENCE_ON_DATE',
  'WAITING_FOR_MEMORIES',
  'WAITING_FOR_MODEL_TO_MATURE',
  'INFERRED_ON_OLD_MODEL',
  'SKIP_FUTURE_DATE',
  'SKIP_UNSAFE_MEMORIES',
  'NEW_MEMORIES_TO_INFER',
  'NO_NEW_MEMORIES_ON_PAST_DATE',
  'WAITING_FOR_SETTLED_MEMORIES',
  'INFERENCE_UP_TO_DATE',
  'TRAINING_PENDING', // Added for pending training, never set server-side
  'TRAINING_REQUEST_IN_QUEUE',
  'TRAINING_RUNNING',
  'INITIAL_TRAINING',
  'RETRY_MODEL_TRAINING',
  'MODEL_NOT_TRAINED',
  'INSUFFICIENT_TRAINING_DATA',
] as const;

const aiStatusActionEnums = [
  'RE_RUN_INFERENCE_ON_DATE',
  'RUN_FIRST_INFERENCE_ON_DATE',
  'RE_RUN_MODEL_TRAINING',
] as const;

/** What is the current state of the AI? */
export type AiState = z.infer<typeof AiStateSchema>;
export const AiStateSchema = z.enum(aiCurrentStatusEnums);

/** What "AI Trigger Action" should fire?  */
export type AiAction = z.infer<typeof AiActionSchema>;
export const AiActionSchema = z.enum(aiStatusActionEnums);

/** Notification format for AI status display */
export type AiStatusNotification = z.infer<typeof AiStatusNotificationSchema>;
export const AiStatusNotificationSchema = z.object({
  title: z.string(),
  message: z.string(),
  variant: z.enum(['info', 'neutral', 'success']),
});

/** Final derived state of the AI model. */
export type DerivedAiStatus = z.infer<typeof DerivedAiStatusSchema>;
export const DerivedAiStatusSchema = z.object({
  currentState: AiStateSchema.nullable(),
  nextAction: AiActionSchema.nullable(),
  notification: AiStatusNotificationSchema.optional(),
  dateLastInferedAt: DateTimeTzSchema.nullable().describe('Last time this day was inferred'),
});
